import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
} from '@backstage/core-components';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import { SidebarLogo } from './SidebarLogo';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import StorageIcon from '@material-ui/icons/Storage';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import SmartToyIcon from '@material-ui/icons/EmojiObjects';
import AssessmentIcon from '@material-ui/icons/Assessment';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import WebIcon from '@material-ui/icons/Web';
import LockIcon from '@material-ui/icons/Lock';
import ExtensionIcon from '@material-ui/icons/Extension';
import BugReportIcon from '@material-ui/icons/BugReport';
import HealingIcon from '@material-ui/icons/LocalHospital';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import CloudIcon from '@material-ui/icons/Cloud';
import WorkIcon from '@material-ui/icons/Work';
import BookIcon from '@material-ui/icons/Book';
import EventIcon from '@material-ui/icons/Event';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import StarIcon from '@material-ui/icons/Star';
import MapIcon from '@material-ui/icons/Map';
import PeopleIcon from '@material-ui/icons/People';
import DescriptionIcon from '@material-ui/icons/Description';
import HomeIcon from '@material-ui/icons/Home';
import CategoryIcon from '@material-ui/icons/Category';
import BuildIcon from '@material-ui/icons/Build';
import DeviceHubIcon from '@material-ui/icons/DeviceHub';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import AssignmentIcon from '@material-ui/icons/Assignment';
import SpeedIcon from '@material-ui/icons/Speed';
import WarningIcon from '@material-ui/icons/Warning';
import CloudQueueIcon from '@material-ui/icons/CloudQueue';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import { makeStyles, Typography } from '@material-ui/core';
import { useMacRole } from './useMacRole';

const useSectionStyles = makeStyles({
  sectionLabel: {
    color: '#f5c518',
    fontSize: '0.65rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    padding: '16px 24px 4px',
  },
});

const SectionLabel = ({ label }: { label: string }) => {
  const classes = useSectionStyles();
  return <Typography className={classes.sectionLabel}>{label}</Typography>;
};

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: ({ navItems }) => {
      const nav = navItems.withComponent(item => (
        <SidebarItem icon={() => item.icon} to={item.href} text={item.title} />
      ));

      // Skipped items — we render these manually
      nav.take('page:search');

      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { isAtLeast } = useMacRole();

      return (
        <Sidebar>
          <SidebarLogo />
          <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
            <SidebarSearchModal />
          </SidebarGroup>
          <SidebarDivider />
          <SidebarGroup label="Menu" icon={<MenuIcon />}>
            <SidebarScrollWrapper>
              {/* Home */}
              <SidebarItem icon={HomeIcon} to="/" text="Home" />
              <SidebarDivider />

              {/* Software Catalog (default Backstage) */}
              {nav.take('page:catalog')}
              {isAtLeast('mac-manager') && nav.take('page:scaffolder')}

              {/* ── Dashboards ── */}
              <SidebarDivider />
              <SectionLabel label="Dashboards" />
              <SidebarItem icon={CategoryIcon} to="/service-catalog" text="Service Catalog" />
              <SidebarItem icon={BuildIcon} to="/self-serve" text="Self-Serve" />
              <SidebarItem icon={DeviceHubIcon} to="/data-model" text="Data Model" />
              <SidebarItem icon={EmojiObjectsIcon} to="/ai-insights" text="AI Insights" />
              <SidebarItem icon={AssignmentIcon} to="/my-work" text="My Work Items" />
              <SidebarItem icon={SpeedIcon} to="/ai-dashboards" text="AI Dashboards" />
              <SidebarItem icon={WarningIcon} to="/incidents" text="Incidents" />
              <SidebarItem icon={CloudQueueIcon} to="/cloud-resources" text="Cloud Resources" />

              {/* ── Platform ── */}
              <SidebarDivider />
              <SectionLabel label="Platform" />
              <SidebarItem icon={StorageIcon} to="/platform/context-lake" text="Context Lake" />
              <SidebarItem icon={PlayArrowIcon} to="/platform/actions" text="Actions" />
              <SidebarItem icon={SmartToyIcon} to="/platform/ai-agents" text="AI Agents" />
              <SidebarItem icon={AssessmentIcon} to="/platform/scorecards" text="Scorecards" />
              <SidebarItem icon={AccountTreeIcon} to="/platform/workflow-orchestrator" text="Workflow Orchestrator" />
              <SidebarItem icon={WebIcon} to="/platform/interface-designer" text="Interface Designer" />
              {isAtLeast('mac-director') && (
                <>
                  <SidebarItem icon={LockIcon} to="/platform/access-controls" text="Access Controls" />
                  <SidebarItem icon={ExtensionIcon} to="/platform/integrations" text="Integrations" />
                </>
              )}
              <SidebarItem icon={DeviceHubIcon} to="/platform/mcp-registry" text="MCP Registry" />

              {/* ── Agentic Solutions ── */}
              {isAtLeast('mac-manager') && (
                <>
                  <SidebarDivider />
                  <SectionLabel label="Agentic Solutions" />
                  <SidebarItem icon={BugReportIcon} to="/agentic/ticket-resolution" text="Ticket Resolution" />
                  <SidebarItem icon={HealingIcon} to="/agentic/self-healing" text="Self-Healing" />
                  <SidebarItem icon={TrendingUpIcon} to="/agentic/engineering-intelligence" text="Eng Intelligence" />
                  <SidebarItem icon={CloudIcon} to="/agentic/resource-management" text="Resource Mgmt" />
                  <SidebarItem icon={WorkIcon} to="/agentic/work-management" text="Work Management" />
                </>
              )}

              {/* ── Resources ── */}
              <SidebarDivider />
              <SectionLabel label="Resources" />
              <SidebarItem icon={BookIcon} to="/resources/blog" text="Blog" />
              <SidebarItem icon={EventIcon} to="/resources/events" text="Events & Webinars" />
              <SidebarItem icon={LibraryBooksIcon} to="/resources/center" text="Resource Center" />

              {/* ── More ── */}
              <SidebarDivider />
              <SidebarItem icon={StarIcon} to="/stories" text="Stories" />
              <SidebarItem icon={DescriptionIcon} to="/docs" text="Docs" />
              <SidebarItem icon={MapIcon} to="/roadmap" text="Public Roadmap" />
              <SidebarItem icon={PeopleIcon} to="/community" text="Community" />

              {/* Remaining auto-discovered nav items */}
              <SidebarDivider />
              {nav.rest({ sortBy: 'title' })}
            </SidebarScrollWrapper>
          </SidebarGroup>
          <SidebarSpace />
          <SidebarDivider />
          <NotificationsSidebarItem />
          <SidebarDivider />
          <SidebarGroup
            label="Settings"
            icon={<UserSettingsSignInAvatar />}
            to="/settings"
          >
            {nav.take('page:app-visualizer')}
            {nav.take('page:user-settings')}
          </SidebarGroup>
        </Sidebar>
      );
    },
  },
});
